import axios, { AxiosHeaders, type InternalAxiosRequestConfig, isAxiosError } from 'axios'
import { useAuthStore } from '@/stores'
import { type Ref } from 'vue'
import { useAppState } from '@/composables/useAppState.ts'
import { type Logger, useLogger } from '@/composables/useLogger.ts'
import { client } from '@/api/client.gen.ts'
import { useErrorDialogService } from '@/services/ErrorDialogService.ts'
import { getActivePinia, Store } from 'pinia'
import { groupBy } from 'lodash-es'

const baseUrl: Ref<URL> = useAppState().backendUrl
const refreshTokenUrl = `/api/token/refresh`
const logger: Logger = useLogger('Axios')

client.instance.defaults.headers.common['Content-Type'] = 'application/ld+json'
client.instance.defaults.headers.common.Accept = 'application/ld+json, application/json, application/problem+json'

// OpenAPI.TOKEN = () => Promise.resolve(useAuthStore().token!)
// OpenAPI.HEADERS = async () => {
//     return {
//         Accept: 'application/ld+json'
//     }
// }
const allowedToFail = ['/webauthn/login/options', '/api/login_check', '/webauth/login/result', '/api/switch-user']
function parseLinkHeader(header: string): string[] {
  const matches = Array.from(header.matchAll(/<([^>]+)>;\s*rel="api-preload"/g))
  return matches.map((match) => match[1])
}

const setup = () => {
  client.setConfig({
    baseURL: baseUrl.value.toString(),
    throwOnError: true
  })
  client.instance.interceptors.request.use(
    (request: InternalAxiosRequestConfig<unknown>) => {
      request.withCredentials = true
      logger.debug('Request interceptor onFulfilled called', request)
      const authStore = useAuthStore()
      const isApiUrl = request.url?.startsWith('/') || request.url?.startsWith(baseUrl.value.toString())
      const isTokenRefreshUrl = request.url === refreshTokenUrl || request.url === `${baseUrl}${refreshTokenUrl}`

      request.headers.set('X-Client-Name', 'Titrop-Client')
      request.headers.set('X-Client-Version', __BUILD_VERSION__)
      request.headers.set('X-Client-Built', __BUILD_DATE__)

      if (isApiUrl) {
        logger.debug('Request interceptor onFulfilled - isApiUrl')
        request.headers['Content-Type'] ??= 'application/ld+json'
        if (authStore.token && !isTokenRefreshUrl) {
          logger.debug('Request interceptor onFulfilled - adding token')
          request.headers.Authorization = `Bearer ${authStore.token}`
        }
        if (authStore.impersonated_user) {
          logger.debug(`Impersonation as ${authStore.impersonated_user} active`)
          request.headers['X-Titrop-Impersonation'] = authStore.impersonated_user
        }
      }
      return request
    },
    async (error) => {
      logger.debug('Request rejected', error)
      return Promise.reject(error)
    }
  )
  client.instance.interceptors.response.use(
    async (response) => {
      if (typeof response.data === 'string' && response.data.includes('<b>Deprecated</b>')) {
        return await client.instance.request(response.config)
      }
      try {
        const headers = response.headers as AxiosHeaders
        const linkHeader = typeof headers.get === 'function' ? headers.get('link') : headers['link']

        if (linkHeader && typeof linkHeader === 'string') {
          const pinia = getActivePinia() as { stores?: () => Store[] }
          if (pinia && typeof pinia.stores == 'function') {
            const stores: { $iriPrefix: string; fetchAllByIri: (args0: unknown) => Promise<unknown> }[] = pinia.stores() as unknown as typeof stores

            const preloadUrls = parseLinkHeader(linkHeader)
            if (preloadUrls.length > 0) {
              const byModel = groupBy(preloadUrls, (u) => u.substring(0, u.lastIndexOf('/')))
              const preloadPromises: Promise<unknown>[] = []
              for (const iriPrefix in byModel) {
                const matchingStore = stores.find((s) => s.$iriPrefix && s.$iriPrefix == iriPrefix)
                if (!matchingStore) continue
                preloadPromises.push(matchingStore.fetchAllByIri(byModel[iriPrefix]))
              }
              await Promise.all(preloadPromises)
            }
          }
        }
      } catch (e) {
        console.warn('Header preload extraction failed', e)
      }
      return response
    },
    async (error) => {
      // if (allowedToFail.includes(error.config.url)) return Promise.reject(error as Error)
      if (allowedToFail.some((path) => error.config.url.startsWith(path))) {
        return Promise.reject(error as Error)
      }
      const authStore = useAuthStore()
      const originalRequest = error.config
      if (error.response?.status && [401, 403].includes(error.response.status)) {
        if (originalRequest.url === refreshTokenUrl) {
          //token refresh failed - do handle here
          return Promise.reject(error as Error)
        }
        if (!originalRequest._retry) {
          logger.warn('Response was firstly rejected due UNAUTH - will retry', error)
          originalRequest._retry = true
          authStore.token = undefined //token was invalid - clear out
          const newToken = await authStore.refreshToken()
          if (newToken) {
            logger.debug('Token refresh succeeded - re-doing request')
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return axios(originalRequest)
          } else {
            logger.error('Token refresh failed', error)
            if (originalRequest.url != '/api/login_check') await authStore.logout()
          }
        } else {
          logger.error('Response was rejected again due UNAUTH', error)

          const newToken = await authStore.refreshToken()
          if (newToken) {
            logger.debug('Second token refresh succeeded - re-doing request')
            originalRequest.headers.Authorization = `Bearer ${newToken}`

            return axios(originalRequest)
          } else {
            logger.error('Seconds token refresh failed - logging out', error)
            await authStore.logout()
          }
        }
      } else {
        if (error.config.throwOnError) logger.error('Unhandled response reject', error)
      }
      if (error.config.throwOnError && (!error || isAxiosError(error))) useErrorDialogService().setAxiosError(error)
      return Promise.reject(error)
    }
  )
  logger.debug('Axios interceptors setup')
}
export default setup
